import { LayoutConfigProvider } from '@newfront-insurance/admin-ui/layout';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useProvider } from '@newfront-insurance/react-provision';
import { useEffect, useState, useRef } from 'react';

interface FloatingButtonProps {
  onClick: () => void;
  hasNotifications?: boolean;
  isTransparent?: boolean;
}

function DragHandle() {
  return (
    <svg width="9" height="16" viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="3" height="3" transform="translate(0 0.5)" fill="#1C2A5A" />
      <rect width="3" height="3" transform="translate(6 0.5)" fill="#1C2A5A" />
      <rect width="3" height="3" transform="translate(0 6.5)" fill="#1C2A5A" />
      <rect width="3" height="3" transform="translate(6 6.5)" fill="#1C2A5A" />
      <rect width="3" height="3" transform="translate(0 12.5)" fill="#1C2A5A" />
      <rect width="3" height="3" transform="translate(6 12.5)" fill="#1C2A5A" />
    </svg>
  );
}

export function FloatingButton({ onClick, hasNotifications, isTransparent }: FloatingButtonProps) {
  // Get the current app from the layout context
  const layoutConfig = useProvider(LayoutConfigProvider);
  const currentApp = layoutConfig?.currentApp;
  const storageKey = currentApp ? `nova-button-position-${currentApp}` : 'nova-button-position';

  const [position, setPosition] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(storageKey);
      return saved ? parseInt(saved, 10) : 36; // Default 36px from right
    }
    return 36;
  });
  const [isDragging, setIsDragging] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dragStartX = useRef(0);
  const startRight = useRef(0);
  const hasDragged = useRef(false);

  /** Adds keyboard shortcuts to the floating button to fire the onClick handler when Command/Ctrl + K is pressed */
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k') {
        // If Command is already pressed when K is pressed, trigger the click
        if (e.metaKey || e.ctrlKey) {
          e.preventDefault();
          onClick();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClick]);

  // Handle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = dragStartX.current - e.clientX;
      const newRight = Math.max(
        0,
        Math.min(window.innerWidth - (buttonRef.current?.offsetWidth || 0), startRight.current + deltaX),
      );
      setPosition(newRight);

      // Mark that we've actually dragged (not just clicked)
      if (Math.abs(deltaX) > 5) {
        hasDragged.current = true;
      }
    };

    const handleMouseUp = () => {
      if (isDragging) {
        setIsDragging(false);
        // Save position to localStorage
        localStorage.setItem(storageKey, position.toString());

        // Prevent the click event if we actually dragged
        if (hasDragged.current) {
          setTimeout(() => {
            hasDragged.current = false;
          }, 100);
        }
      }
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, position]);

  // Ensure the button remains visible within the viewport when zoom/resize changes
  useEffect(() => {
    const ensureVisible = () => {
      const buttonWidth = buttonRef.current?.offsetWidth || 0;
      const maxRight = Math.max(0, window.innerWidth - buttonWidth);

      setPosition((prev) => {
        const clamped = Math.max(0, Math.min(prev, maxRight));
        if (clamped !== prev) {
          try {
            localStorage.setItem(storageKey, clamped.toString());
          } catch {
            // noop
          }
        }
        return clamped;
      });
    };

    // Run after initial render to ensure we can measure the element
    const raf = requestAnimationFrame(ensureVisible);
    window.addEventListener('resize', ensureVisible);
    window.addEventListener('orientationchange', ensureVisible);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', ensureVisible);
      window.removeEventListener('orientationchange', ensureVisible);
    };
  }, [storageKey]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    startRight.current = position;
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label="Nova Assist"
      className={cn(
        'fixed bottom-0 z-[1000]',
        'h-10 w-fit',
        'rounded-t-sm shadow-lg',
        'text-steel-100',
        'shadow-floatingBar',
        'flex items-center justify-between',
        'text-sm font-semibold',
        'select-none',
        'p-0',
        'hover:shadow-rebrand-400/20 focus:outline-hidden',
        {
          'cursor-move': isDragging,
          // Make the floating button transparent/click through. Chat can still be opened by pressing the command+k shortcut.
          'pointer-events-none opacity-0': isTransparent,
        },
      )}
      style={{
        right: `${position}px`,
        background: 'linear-gradient(97deg, #A4F5FF 0%, #E7FC53 122.24%)',
        boxShadow: '3.013px 3.013px 24.1px 0 rgba(38, 45, 70, 0.15)',
      }}
      onClick={(e) => {
        // Prevent click if we just finished dragging
        if (hasDragged.current) {
          e.preventDefault();
          return;
        }
        onClick();
      }}
    >
      <div className="flex h-full w-full items-center justify-between">
        <div
          className="flex h-full items-center px-2 transition-colors"
          role="button"
          tabIndex={0}
          aria-label="Drag to move"
          onMouseDown={handleMouseDown}
          onKeyDown={(e) => {
            // Optionally, allow keyboard drag with space/enter
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              setIsDragging(true);
              dragStartX.current = 0;
              startRight.current = position;
            }
          }}
        >
          <DragHandle />
        </div>
        <div className="flex items-center gap-2 px-4">
          <span className="font-semibold tracking-widest text-[#1C2A5A]">Nova Assist</span>
        </div>
        <span
          className={cn([
            'bg-rebrand-400 absolute top-[-8px] right-[-4px] h-[16px] w-[16px] rounded-full border-2 border-white',
            {
              block: hasNotifications,
              hidden: !hasNotifications,
            },
          ])}
        />
      </div>
    </button>
  );
}
